import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchInspectionCampaign } from '../api/inspectionCampaigns';
import {
  fetchInspectionReview,
  markCampaignReviewed,
  saveInspectionTour,
  type InspectionCapture,
  type InspectionTour,
  type InspectionTourRequest,
} from '../api/inspections';
import { fetchSession, type SessionFull } from '../api/missions';
import { ca } from '../i18n/ca';
import { useCanReview } from './reviewAccess';
import { buildReviewItems, groupCaptures, type ReviewItem } from './inspectionGroups';
import { PhotoViewer } from './PhotoViewer';
import { ReviewToolbar } from './ReviewToolbar';
import { Shell } from './Shell';
import { errorText } from './useSessionLive';
import { TourChapter } from './TourChapter';
import { tourReason } from './tourReason';
import { useCaptureSave } from './useCaptureSave';
import { useConfirm } from './useConfirm';
import { useReviewFlow } from './useReviewFlow';

interface TourState {
  index: number;
  /** The session code, or a synthetic key for an item that has not started one yet — always unique across the campaign. */
  key: string;
  sessionCode: string | null;
  recipeTitle: string;
  worldClass: string;
  state: string;
  itemError: string | null;
  session: SessionFull | null;
  captures: InspectionCapture[];
  autoFindings: string[];
  tour: InspectionTour | null;
}

interface Chapter extends TourState {
  localItems: ReviewItem[];
  /** This chapter's own first index in the campaign-wide, concatenated item list the viewer steps through. */
  offset: number;
  /** A tour that needs no review: folded, out of the counts and out of the viewer's walk, until it is asked for. */
  skipped: boolean;
}

/**
 * The whole batch of an inspection campaign's tours, reviewed from ONE page
 * ({@code /proves/inspeccions/campanya/:id/revisio}, the owner's own request
 * 2026-09-27: "group the whole batch of tests into ONE review document, not
 * one per tour"): every tour's own mission facts and capture grid
 * (`TourChapter`, the same component the per-tour page uses, so nothing
 * about a capture's controls or a caption differs here), one after another
 * as chapters, a chapter index to jump to any of them, one progress counter
 * for the whole campaign, and a single full-screen viewer whose ←/→ (and the
 * "N" next-pending shortcut) walk every tour's items concatenated in item
 * order — crossing from the last capture of one tour straight into the first
 * of the next.
 *
 * A tour that needs no review (superseded by a newer campaign, old, empty,
 * set aside; the owner, 2026-09-29) is a folded chapter that says why, and
 * is neither counted nor walked: what the page asks of a person is what is
 * still worth a look.
 */
export function InspectionCampaignReview() {
  const { id } = useParams<{ id: string }>();
  const t = ca.inspectionCampaignReviewPage;
  const r = ca.inspectionReviewPage;
  const canReview = useCanReview();
  const [tours, setTours] = useState<TourState[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [marking, setMarking] = useState(false);
  const [confirm, confirmDialog] = useConfirm();

  useEffect(() => {
    if (!id) return;
    fetchInspectionCampaign(id)
      .then((campaign) =>
        Promise.all(
          campaign.items.map((it): Promise<TourState> => {
            if (!it.sessionCode) {
              return Promise.resolve({
                index: it.index,
                key: `item-${it.index}`,
                sessionCode: null,
                recipeTitle: it.recipeTitle,
                worldClass: it.worldClass,
                state: it.state,
                itemError: it.error,
                session: null,
                captures: [],
                autoFindings: [],
                tour: null,
              });
            }
            const code = it.sessionCode;
            return Promise.all([
              fetchSession(code, 'direction').catch(() => null),
              fetchInspectionReview(code).catch(() => null),
            ]).then(([session, review]) => ({
              index: it.index,
              key: code,
              sessionCode: code,
              recipeTitle: it.recipeTitle,
              worldClass: it.worldClass,
              state: it.state,
              itemError: it.error,
              session,
              captures: review?.captures ?? [],
              autoFindings: review?.autoFindings ?? [],
              tour: review?.tour ?? null,
            }));
          }),
        ),
      )
      .then(setTours)
      .catch((e) => setError(errorText(e)));
  }, [id]);

  const chapters = useMemo<Chapter[]>(() => {
    let offset = 0;
    return (tours ?? []).map((tr) => {
      const skipped = tr.tour?.tray === 'skip';
      const localItems = skipped ? [] : buildReviewItems(tr.session?.definition, tr.captures);
      const chapter: Chapter = { ...tr, localItems, offset, skipped };
      offset += localItems.length;
      return chapter;
    });
  }, [tours]);

  const mergedItems = useMemo(() => chapters.flatMap((c) => c.localItems), [chapters]);
  const allCaptures = useMemo(
    () => chapters.filter((c) => !c.skipped).flatMap((c) => c.captures),
    [chapters],
  );

  const patch = useCallback((uploadId: number, change: Partial<InspectionCapture>) => {
    setTours((all) =>
      (all ?? []).map((tr) =>
        tr.captures.some((c) => c.uploadId === uploadId)
          ? {
              ...tr,
              captures: tr.captures.map((c) => (c.uploadId === uploadId ? { ...c, ...change } : c)),
            }
          : tr,
      ),
    );
  }, []);
  const { save, savingId } = useCaptureSave(patch, setError, canReview);

  const saveTour = useCallback(
    (sessionCode: string, body: InspectionTourRequest) => {
      if (!canReview) return;
      saveInspectionTour(sessionCode, body)
        .then((tour) =>
          setTours((all) =>
            (all ?? []).map((tr) => (tr.sessionCode === sessionCode ? { ...tr, tour } : tr)),
          ),
        )
        .catch((e) => setError(errorText(e)));
    },
    [canReview],
  );

  const waiting = (tours ?? []).filter((tr) => tr.tour?.tray === 'review').length;
  const allReviewed = waiting === 0 && (tours ?? []).some((tr) => tr.tour?.tray === 'done');

  // Every tour still waiting closed as reviewed at once (the owner, 2026-09-30):
  // a campaign of two hundred pictures is looked at long before each is marked.
  const markAll = useCallback(async () => {
    if (!id || !canReview || waiting === 0) return;
    const yes = await confirm({
      title: t.markAllTitle,
      body: t.markAllBody(waiting),
      confirmLabel: t.markAllReviewed,
    });
    if (!yes) return;
    setMarking(true);
    markCampaignReviewed(id)
      .then((after) => {
        const byCode = new Map(after.map((tour) => [tour.sessionCode, tour]));
        setTours((all) =>
          (all ?? []).map((tr) =>
            tr.sessionCode && byCode.has(tr.sessionCode)
              ? { ...tr, tour: byCode.get(tr.sessionCode) ?? tr.tour }
              : tr,
          ),
        );
      })
      .catch((e) => setError(errorText(e)))
      .finally(() => setMarking(false));
  }, [id, canReview, waiting, confirm, t]);

  const flow = useReviewFlow(mergedItems, save);
  const { viewerIndex, gridIndex: globalGridIndex } = flow;

  const markGroupCorrect = useCallback(
    (tourKey: string, groupKey: string) => {
      const tour = chapters.find((c) => c.key === tourKey);
      if (!tour) return;
      const group = groupCaptures(tour.captures).find((g) => g.key === groupKey);
      if (!group) return;
      for (const c of group.captures) {
        if (!c.flags.includes('correct')) save(c.uploadId, [...c.flags, 'correct'], c.note ?? '');
      }
    },
    [chapters, save],
  );

  if (!id) return null;

  const totalCaptures = allCaptures.length;
  const reviewedCount = allCaptures.filter((c) => c.reviewedBy).length;
  const problemCount = allCaptures.filter((c) => c.flags.includes('problem')).length;

  return (
    <Shell title={t.title(id)} width="wide">
      <p>
        <Link to="/proves">← {ca.testsPage.back}</Link>
        {' · '}
        <Link to={`/proves/inspeccions/campanya/${id}`}>{t.back}</Link>
      </p>
      {confirmDialog}
      {canReview && waiting > 0 && (
        <p className="row actions">
          <button
            type="button"
            className="primary"
            disabled={marking}
            onClick={() => void markAll()}
          >
            <i className="bi bi-check2-all" aria-hidden="true" /> {t.markAllReviewed}
          </button>
        </p>
      )}
      {allReviewed && <p className="muted small">{t.markAllDone}</p>}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {tours === null ? (
        <p className="muted small">{t.loading}</p>
      ) : chapters.length === 0 ? (
        <p className="muted small">{t.noTours}</p>
      ) : (
        <>
          <ReviewToolbar
            total={totalCaptures}
            reviewed={reviewedCount}
            problems={problemCount}
            filter={flow.filter}
            onFilter={flow.setFilter}
            onQueue={flow.openQueue}
          />
          <section className="card" aria-label={t.chapterIndex}>
            <h2>{t.chapterIndex}</h2>
            <ol className="chapter-index">
              {chapters.map((c) => (
                <li key={c.key}>
                  <a href={`#chapter-${c.key}`}>{t.chapterTitle(c.recipeTitle, c.worldClass)}</a>
                  {c.tour && (
                    <>
                      {' '}
                      <span
                        className={`chip small ${c.tour.tray === 'done' ? 'ok' : c.tour.tray === 'review' ? 'warn' : ''}`}
                      >
                        {r.tour.states[c.tour.state] ?? c.tour.state}
                      </span>
                    </>
                  )}
                </li>
              ))}
            </ol>
          </section>
          {chapters.map((c) =>
            c.skipped && c.tour ? (
              <section
                key={c.key}
                id={`chapter-${c.key}`}
                className="campaign-review-chapter skipped"
              >
                <div className="row between">
                  <h2>{t.chapterTitle(c.recipeTitle, c.worldClass)}</h2>
                  <span className="chip">{r.tour.states[c.tour.state] ?? c.tour.state}</span>
                </div>
                <p className="muted">{tourReason(c.tour)}</p>
                <p className="row actions">
                  {canReview && c.tour.reason !== 'no_captures' && c.sessionCode && (
                    <button
                      type="button"
                      onClick={() => saveTour(c.sessionCode as string, { reopened: true })}
                    >
                      {c.tour.dismissed ? r.tour.undoDismiss : r.tour.reviewAnyway}
                    </button>
                  )}
                  {c.sessionCode && (
                    <Link to={`/proves/inspeccio/${c.sessionCode}`} className="small">
                      {t.openTourAlone}
                    </Link>
                  )}
                </p>
              </section>
            ) : (
              <section key={c.key} id={`chapter-${c.key}`} className="campaign-review-chapter">
                <div
                  className="row"
                  style={{ justifyContent: 'space-between', alignItems: 'baseline' }}
                >
                  <h2>{t.chapterTitle(c.recipeTitle, c.worldClass)}</h2>
                  {c.sessionCode && (
                    <Link to={`/proves/inspeccio/${c.sessionCode}`} className="small">
                      {t.openTourAlone}
                    </Link>
                  )}
                </div>
                <TourChapter
                  code={c.key}
                  session={c.session}
                  captures={c.captures}
                  items={c.localItems}
                  autoFindings={c.autoFindings}
                  gridIndex={
                    viewerIndex === null &&
                    globalGridIndex >= c.offset &&
                    globalGridIndex < c.offset + c.localItems.length
                      ? globalGridIndex - c.offset
                      : -1
                  }
                  viewerOpen={viewerIndex !== null}
                  onOpenViewer={(localIndex) => flow.setViewerIndex(c.offset + localIndex)}
                  onFocusIndex={(localIndex) => flow.setFocusedIndex(c.offset + localIndex)}
                  onMarkGroupCorrect={(groupKey) => markGroupCorrect(c.key, groupKey)}
                  onToggleFlag={flow.toggleFlag}
                  onNote={flow.saveNote}
                  savingId={savingId}
                  filter={flow.filter}
                  tour={c.tour}
                  onTour={
                    c.sessionCode ? (body) => saveTour(c.sessionCode as string, body) : undefined
                  }
                />
              </section>
            ),
          )}
        </>
      )}
      {viewerIndex !== null && (
        <PhotoViewer
          items={mergedItems}
          index={viewerIndex}
          onClose={() => flow.setViewerIndex(null)}
          onIndex={flow.setViewerIndex}
          onToggleFlag={flow.toggleFlag}
          onNote={flow.saveNote}
          advance={flow.advance}
          onAdvance={flow.setAdvance}
          onModerated={(uploadId, outcome) => {
            if (outcome === 'deleted') flow.setViewerIndex(null);
            setTours((all) =>
              (all ?? []).map((tr) => ({
                ...tr,
                captures:
                  outcome === 'deleted'
                    ? tr.captures.filter((c) => c.uploadId !== uploadId)
                    : tr.captures.map((c) =>
                        c.uploadId === uploadId ? { ...c, withheld: false } : c,
                      ),
              })),
            );
          }}
          savingNote={(() => {
            const at = mergedItems[viewerIndex];
            return at?.type === 'capture' ? savingId === at.capture.uploadId : false;
          })()}
        />
      )}
    </Shell>
  );
}
